import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Wrench, CheckCircle2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

export function FixItModal({ open, onOpenChange, defaultWebsite = "" }) {
  const [form, setForm] = useState({ name: "", email: "", website: defaultWebsite, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/service/book", form);
      setDone(true);
      toast.success("Request sent! Our experts will reach out shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send request.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setForm({ name: "", email: "", website: defaultWebsite, message: "" }); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#0C1424] border border-slate-700 text-white sm:max-w-md" data-testid="fixit-modal">
        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="text-green-400 mx-auto mb-4" size={48} />
            <DialogTitle className="font-heading text-xl">You're in good hands</DialogTitle>
            <p className="text-slate-400 text-sm mt-2">
              A Warden security expert will contact you within 1 business day to fix the issues on your site.
            </p>
            <button
              onClick={() => close(false)}
              data-testid="fixit-done-button"
              className="mt-6 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold px-6 py-2.5 rounded-md transition-all"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="w-11 h-11 rounded-md bg-cyanbrand/10 border border-cyanbrand/25 flex items-center justify-center mb-2">
                <Wrench size={22} className="text-cyanbrand" />
              </div>
              <DialogTitle className="font-heading text-xl">Fix it for me</DialogTitle>
              <DialogDescription className="text-slate-400">
                Let our experts fix your security issues for you — done-for-you remediation, no tech skills needed.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-2" data-testid="fixit-form">
              <input
                data-testid="fixit-name" required value={form.name} onChange={set("name")} placeholder="Your name"
                className="w-full bg-[#030812] border border-slate-800 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyanbrand text-sm"
              />
              <input
                data-testid="fixit-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com"
                className="w-full bg-[#030812] border border-slate-800 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyanbrand text-sm"
              />
              <input
                data-testid="fixit-website" required value={form.website} onChange={set("website")} placeholder="yourwebsite.com"
                className="w-full bg-[#030812] border border-slate-800 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyanbrand text-sm font-mono"
              />
              <textarea
                data-testid="fixit-message" value={form.message} onChange={set("message")} rows={3} placeholder="Anything we should know? (optional)"
                className="w-full bg-[#030812] border border-slate-800 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyanbrand text-sm resize-none"
              />
              <button
                data-testid="fixit-submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold py-3 rounded-md transition-all disabled:opacity-60"
                style={{ boxShadow: "0 0 15px rgba(0,229,255,0.3)" }}
              >
                {loading && <Loader2 size={18} className="animate-spin" />} Request expert help
              </button>
              <p className="text-xs text-slate-600 text-center">Free consultation · no obligation</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
